import { ORDER_STATUSES, type OrderStatusKey } from '@c214/shared';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { UserOrdersPanel } from '../components/admin/UserOrdersPanel';
import { Button, buttonVariants } from '../components/ui/button';
import { Chip } from '../components/ui/chip';
import { Input } from '../components/ui/input';
import { Pagination } from '../components/ui/pagination';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { useAuth } from '../hooks/useAuth';
import { useAuthModal } from '../providers';

export function AdminUsersPage() {
  const { data: user, isLoading: authLoading } = useAuth();
  const authModal = useAuthModal();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statuses, setStatuses] = useState<OrderStatusKey[]>([]);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggleExpanded = (userId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Пошук за текстом і фільтр за статусом — взаємовиключні: увімкнення одного
  // скидає інший, щоб не плутати, за яким критерієм зараз відфільтровано.
  // Обидва скидають пагінацію на 1-шу сторінку — інакше можна залишитись на
  // сторінці, якої вже нема в новому, звуженому результаті.
  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (value) setStatuses([]);
    setPage(1);
  };

  const toggleStatus = (status: OrderStatusKey) => {
    setQuery('');
    setStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
    setPage(1);
  };

  const { data, isLoading, isError } = useAdminUsers(debouncedQuery, statuses, page);
  const users = data?.users ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / (data?.pageSize ?? 10)));

  if (authLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;

  if (!user) {
    return (
      <div className="section-pad text-center">
        <p className="mb-4 text-[15px] text-muted">Увійдіть, щоб переглянути користувачів.</p>
        <Button variant="accent" size="lg" onClick={() => authModal.open('login')}>
          УВІЙТИ
        </Button>
      </div>
    );
  }

  if (user.role !== 'admin') return <Navigate to="/" replace />;

  const exportParams = new URLSearchParams();
  if (debouncedQuery) exportParams.set('query', debouncedQuery);
  if (statuses.length) exportParams.set('statuses', statuses.join(','));
  const exportUrl = `/api/admin/users/export?${exportParams}`;

  return (
    <div className="section-pad">
      <div className="mb-8 flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="m-0 text-[clamp(32px,6vw,72px)] font-extrabold tracking-[-0.045em]">
          Користувачі
        </h1>
        <a href={exportUrl} className={buttonVariants({ variant: 'accent', size: 'md' })}>
          ЕКСПОРТУВАТИ В PDF
        </a>
      </div>

      <Input
        placeholder="Пошук за email або ім'ям"
        value={query}
        onChange={(e) => handleQueryChange(e.target.value)}
        className="mb-3 max-w-[400px]"
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="mono-label">СТАТУС ЗАМОВЛЕНЬ:</span>
        {ORDER_STATUSES.map((option) => (
          <Chip
            key={option.key}
            active={statuses.includes(option.key)}
            onClick={() => toggleStatus(option.key)}
          >
            {option.label}
          </Chip>
        ))}
      </div>

      {isLoading && <p className="mono-label">ЗАВАНТАЖЕННЯ…</p>}
      {isError && <p className="mono-label">Не вдалося завантажити користувачів.</p>}
      {!isLoading && !isError && users.length === 0 && (
        <p className="text-[15px] text-muted">Нікого не знайдено.</p>
      )}
      {!isLoading && !isError && users.length > 0 && (
        <>
          <div className="divide-y divide-line-soft border border-line">
            {users.map((u) => {
              const isExpanded = expanded.has(u.id);
              return (
                <div key={u.id}>
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => toggleExpanded(u.id)}
                    className="flex w-full flex-wrap items-center justify-between gap-4 border-none bg-transparent px-6 py-4 text-left cursor-pointer hover:bg-line-soft"
                  >
                    <div>
                      <p className="text-[15px] font-semibold text-ink">{u.name ?? u.email}</p>
                      <p className="mt-1 text-[13px] text-muted">{u.email}</p>
                      {u.phone && <p className="mt-1 text-[13px] text-muted">{u.phone}</p>}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="mono-label">
                        {u.role === 'admin' ? 'АДМІН' : 'КОРИСТУВАЧ'}
                      </span>
                      <span className="mono-label">
                        {statuses.length > 0 ? 'У ЦЬОМУ СТАТУСІ' : 'ЗАМОВЛЕНЬ'}: {u.ordersCount}
                      </span>
                      <span className="w-3 text-[12px] text-muted" aria-hidden="true">
                        {isExpanded ? '▾' : '▸'}
                      </span>
                    </div>
                  </button>
                  {isExpanded && <UserOrdersPanel userId={u.id} />}
                </div>
              );
            })}
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}
